// @flow

import * as React from 'react';
import { connect } from 'react-redux';
import classnames from '@ta/common.classnames';
import { Impression } from '@ta/common.tracking';
import GlobalNavApiBinding from '@ta/brand.global-nav';
import type { NavLinkType } from '@ta/brand.global-nav';
import { GeoPillSponsor } from '@ta/brand.sponsored-geopill';

import QuickLinkTileItem from './QuickLinkTileItem';
import type { QuickLinksMoreMenuData } from './types';
import styles from './QuickLinkTiles.less';

type Props = {|
  /** Links to show */
  links: ?(NavLinkType[]),

  /** ID of the NavLinkType[] that should show as active */
  activeLinkId: ?string,

  /** Whether we should show the more tile on a full, new line, such as when we want to show curated shopping as a visible, 6th link. */
  extendedQuickLinks: boolean,

  /** Whether the page we are on is scoped to a geo. */
  isGeoScoped: boolean,

  /** The locationId of the geoscope. 1 if unscoped */
  geoId: number,

  /**
   *
   */
  isMobileWeb: boolean,

  /**
   * Whether the feed is scoped to 'Nearby'
   */
  isNearbyScope: boolean,

  /** uid of the page */
  uid: string,

  /** Which color scheme to use */
  color: 'green' | 'black',
|};

/** The tiles version of quick links. As currently seen on desktop homepage and mobile landers. */
class QuickLinkTiles extends React.Component<Props> {
  static defaultProps = {
    color: 'green',
  };

  constructor(props: Props) {
    super(props);
    this.containerEl = React.createRef();
    this.checkOffset = -1;
  }

  /**
   * Since we need to show the pinned menu, we need to hide it when the user scrolls back up..
   * this is necessary because usually the user has to scroll partially through the first card to
   * see the menu getting pinned.
   */
  scrollHandler() {
    if (this.checkOffset > 0) {
      // eslint-disable-next-line no-restricted-globals
      const { pageYOffset } = window;

      if (pageYOffset < this.checkOffset) {
        this.checkOffset = -1;
      }
    }
  }

  containerEl: any;
  checkOffset: number;

  render() {
    const { links } = this.props;
    if (!links) {
      return null;
    }

    const visibleLinks = links.filter(link => !link.forceCollapse);
    const collapsedLinks = links.filter(link => link.forceCollapse);
    const collapsedData: QuickLinksMoreMenuData = {
      links: collapsedLinks,
      activeLinkId: this.props.activeLinkId,
    };

    // Visible links + 1 more link
    const numTiles = visibleLinks.length + 1;
    // On MW, we have 3 tiles per row. In the case that the rows are entirely filled by visible links (not more menu),
    // the more menu will be on its own line, so we don't need to fill with placeholder tiles. In the case where
    // a row is missing tiles, we fill in so we get a full row, including the more menu.
    const moreOwnLine = visibleLinks.length % 3 === 0;
    const numPlaceholderTiles = moreOwnLine ? 0 : Math.max(0, (3 - (numTiles % 3)) % 3);

    const classes = classnames(
      'ui_columns',
      'is-mobile',
      'is-multiline',
      'is-gapless',
      // styles.quickLinks does exist, but it's prefixed with :global(.is-gapless) which is probably why this rule is confused.
      // eslint-disable-next-line css-modules/no-undef-class
      styles.quickLinks,
    );

    const content = (
      <div className={classes}>
        <GeoPillSponsor adAreaId="quicklinks">
          {sponsorDataProvider => (
            <Impression
              source="homeQuickLinks"
              attributes={{
                homeQuickLinks: {
                  uid: this.props.uid,
                  geoScopeId: this.props.geoId,
                  isNearbyScope: this.props.isNearbyScope,
                  quickLinks: links.map(link => ({
                    isCollapsed: link.forceCollapse,
                    quickLinkType: link.id.toUpperCase(),
                  })),
                },
              }}
            >
              {visibleLinks.map(link => (
                <QuickLinkTileItem
                  link={link}
                  isActive={!!this.props.activeLinkId && this.props.activeLinkId === link.id}
                  key={link.id}
                  isGeoScoped={this.props.isGeoScoped}
                  extendedQuickLinks={this.props.extendedQuickLinks}
                  color={this.props.color}
                  sponsorDataProvider={sponsorDataProvider}
                />
              ))}
              <QuickLinkTileItem
                isMoreTile
                extendedQuickLinks={this.props.extendedQuickLinks}
                color={this.props.color}
                collapsedData={collapsedData}
                alignMoreRight={numTiles % 3 === 0}
                sponsorDataProvider={sponsorDataProvider}
              />
              {Array.from(Array(numPlaceholderTiles).keys(), i => (
                <QuickLinkTileItem key={i} isPlaceholderTile color={this.props.color} />
              ))}
            </Impression>
          )}
        </GeoPillSponsor>
      </div>
    );

    // We set rootMargin-bottom to some arbitrarily high number so that we don't show the quick links until you scroll past them, as opposed to showing before them.
    return <div ref={this.containerEl}>{content}</div>;
  }
}

// $FlowFixMe: Seems to be an issue with bind/flow being immediately wrapped by connect but this seems fine to me.
export default connect(state => ({
  isMobileWeb: state.meta.device.viewportCategory === 'MOBILE',
  isNearbyScope: state.route.isNearby,
  geoId: state.page.geoId,
  uid: state.tracking.uid || '',
}))(GlobalNavApiBinding(QuickLinkTiles));
